import React, { useEffect, useState } from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CreditCard, Smartphone, Wallet, Star } from 'lucide-react';
import { fetchSavedMethods } from '../services/paymentApi';
import { maskCardNumber } from '../utils/formatters';
import { cn } from '@/lib/utils';

const METHOD_ICON = {
    card: CreditCard,
    upi: Smartphone,
    wallet: Wallet,
};

/**
 * Lists a user's saved payment methods with selection and pay button.
 * @param {{ token: string, onSubmit: Function, isProcessing: boolean }} props
 */
export function SavedMethodsList({ token, onSubmit, isProcessing }) {
    const [methods, setMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState('');

    useEffect(() => {
        if (!token) { setLoading(false); return; }
        fetchSavedMethods(token)
            .then((data) => {
                setMethods(Array.isArray(data) ? data : []);
                const def = data?.find(m => m.isDefault);
                if (def) setSelected(def.id);
            })
            .catch(() => setMethods([]))
            .finally(() => setLoading(false));
    }, [token]);

    const handlePay = () => {
        if (!selected) return;
        onSubmit({ savedMethodId: selected });
    };

    if (loading) {
        return (
            <div className="space-y-3" data-testid="saved-methods-loading">
                {[1, 2].map((i) => <Skeleton key={i} className="h-16 w-full rounded-sm" />)}
            </div>
        );
    }

    if (!methods.length) {
        return (
            <div
                className="text-center py-12 text-slate-500"
                data-testid="saved-methods-empty"
            >
                <CreditCard className="w-12 h-12 mx-auto mb-3 text-slate-300" aria-hidden="true" />
                <p className="font-medium text-slate-700">No saved payment methods</p>
                <p className="text-sm mt-1">Pay with a card or UPI to save it for next time.</p>
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="saved-methods-list">
            <RadioGroup
                value={selected}
                onValueChange={setSelected}
                aria-label="Saved payment methods"
                className="space-y-3"
            >
                {methods.map((method) => {
                    const Icon = METHOD_ICON[method.type] || CreditCard;
                    return (
                        <Label
                            key={method.id}
                            htmlFor={`saved-${method.id}`}
                            className={cn(
                                'flex items-center gap-4 p-4 border rounded-sm cursor-pointer transition-all duration-200',
                                selected === method.id
                                    ? 'border-[#F87217] bg-[#F87217]/5'
                                    : 'border-slate-200 hover:border-slate-300'
                            )}
                        >
                            <RadioGroupItem value={method.id} id={`saved-${method.id}`} />
                            <Icon className="w-5 h-5 text-slate-500 flex-shrink-0" aria-hidden="true" />
                            <div className="flex-1 min-w-0">
                                {method.type === 'card' && (
                                    <>
                                        <p className="font-medium text-slate-900">
                                            {method.brand ? `${method.brand} ` : ''}•••• {method.lastFour}
                                        </p>
                                        <p className="text-xs text-slate-500">Expires {method.expiryDate}</p>
                                    </>
                                )}
                                {method.type === 'upi' && (
                                    <p className="font-medium text-slate-900">{method.upiId}</p>
                                )}
                                {method.type === 'wallet' && (
                                    <p className="font-medium text-slate-900">{method.walletName}</p>
                                )}
                            </div>
                            {method.isDefault && (
                                <Badge
                                    variant="secondary"
                                    className="bg-[#F87217]/10 text-[#F87217] border-0 text-xs"
                                >
                                    <Star className="w-3 h-3 mr-1" aria-hidden="true" />
                                    Default
                                </Badge>
                            )}
                        </Label>
                    );
                })}
            </RadioGroup>

            <Button
                type="button"
                disabled={!selected || isProcessing}
                onClick={handlePay}
                className="w-full bg-[#F87217] hover:bg-[#C65B12] text-white rounded-sm btn-primary-glow font-semibold py-6"
                data-testid="submit-saved-method"
            >
                {isProcessing ? (
                    <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Processing...
                    </span>
                ) : 'Pay with Saved Method'}
            </Button>
        </div>
    );
}

export default SavedMethodsList;
